import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const TourContext = createContext(null);
const STORAGE_KEY = "launchpad_tour_seen_v1";

/* Steps for the onboarding tour. `selector` is a CSS selector;
   if `null`, the step is rendered as a centered welcome modal. */
export const TOUR_STEPS = [
  {
    id: "welcome",
    selector: null,
    title: "Bienvenido a LaunchPad",
    body: "En 30 segundos te muestro las funciones más útiles. Pulsa Siguiente para empezar.",
    placement: "center",
    accent: "#2563EB",
  },
  {
    id: "sidebar",
    selector: '[data-testid="sidebar"]',
    title: "Tus plataformas",
    body: "Cada plataforma agrupa botones por contexto: Trabajo, Personal, Gaming. Cámbialas con un click.",
    placement: "right",
    accent: "#2563EB",
  },
  {
    id: "add-deck",
    selector: '[data-testid="add-deck-btn"]',
    title: "Crear plataforma",
    body: "Pulsa + para añadir una nueva plataforma con su propio nombre, ícono y color.",
    placement: "right",
    accent: "#FACC15",
  },
  {
    id: "paste-create",
    selector: '[data-testid="paste-create-btn"]',
    title: "Pegar y crear",
    body: "Copia un URL, pulsa este botón, y se crea una tarjeta enriquecida con imagen automática.",
    placement: "bottom",
    accent: "#2563EB",
  },
  {
    id: "share",
    selector: '[data-testid="share-open-btn"]',
    title: "Compartir",
    body: "Comparte LaunchPad por SMS, WhatsApp o Teams con un solo click.",
    placement: "bottom",
    accent: "#FACC15",
  },
  {
    id: "search",
    selector: '[data-testid="search-btn"]',
    title: "Buscar todo (⌘K)",
    body: "Encuentra cualquier botón o plataforma al instante con el atajo ⌘K / Ctrl+K.",
    placement: "bottom",
    accent: "#2563EB",
  },
  {
    id: "add-tile",
    selector: '[data-testid="add-tile-btn"]',
    title: "Crear un botón",
    body: "El + dentro del panel abre el editor: nombre, URL, color, ícono y tamaño.",
    placement: "top",
    accent: "#FACC15",
  },
  {
    id: "drag",
    selector: null,
    title: "Drag & Drop completo",
    body: "Arrastra links desde otras pestañas, reordena tarjetas, muévelas entre plataformas, y reordena las plataformas con el handle ⋮⋮.",
    placement: "center",
    accent: "#EF4444",
  },
  {
    id: "done",
    selector: null,
    title: "¡Listo!",
    body: "Todo tuyo. Puedes repetir este tour cuando quieras pulsando el botón ? en la barra superior.",
    placement: "center",
    accent: "#FACC15",
  },
];

export function TourProvider({ children }) {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);

  // Auto-start tour for new users (after a brief delay so layout settles)
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const seen = window.localStorage.getItem(STORAGE_KEY);
    if (seen) return undefined;
    const t = setTimeout(() => {
      // Only auto-start if we're on the dashboard
      if (window.location.pathname === "/dashboard") {
        setActive(true);
        setStep(0);
      }
    }, 900);
    return () => clearTimeout(t);
  }, []);

  const finish = useCallback(() => {
    setActive(false);
    setStep(0);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
  }, []);

  const start = useCallback(() => {
    setStep(0);
    setActive(true);
  }, []);

  const next = useCallback(() => {
    setStep((s) => {
      if (s + 1 >= TOUR_STEPS.length) {
        finish();
        return s;
      }
      return s + 1;
    });
  }, [finish]);

  const prev = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  const value = useMemo(
    () => ({
      active,
      step,
      total: TOUR_STEPS.length,
      currentStep: TOUR_STEPS[step] || null,
      start,
      next,
      prev,
      skip: finish,
    }),
    [active, step, start, next, prev, finish]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within TourProvider");
  return ctx;
}
